import { React, PropTypes } from 'imports';
import DropPanel from '../DropPanel/DropPanel';
import TextInput from '../TextInput/TextInput';
import Listbox, { valuePropType as idPropType } from 'typed/components/common/Listbox/Listbox';
import { toArray, isAncestorOrSelf } from 'utils';
import './EditableSelect.less';

export { idPropType };

export class Label extends React.PureComponent {
    static get displayName() {
        return 'EditableSelect.Label';
    }

    static get propTypes() {
        return Listbox.Label.propTypes;
    }

    static get defaultProps() {
        return Listbox.Label.defaultProps;
    }

    render() {
        throw new Error('EditableSelect.Label can be used only inside EditableSelect');
    }
}

export class Option extends React.PureComponent {
    static get displayName() {
        return 'EditableSelect.Option';
    }

    static get propTypes() {
        return {
            ...Listbox.Option.propTypes,
            id: idPropType,
            value: PropTypes.string.isRequired
        }
    }

    static get defaultProps() {
        return Listbox.Option.defaultProps;
    }

    render() {
        throw new Error('EditableSelect.Option can be used only inside EditableSelect');
    }
}

export default class EditableSelect extends React.PureComponent {
    static get idPropType() {
        return idPropType;
    }

    static get Label() {
        return Label;
    }

    static get Option() {
        return Option;
    }

    static get propTypes() {
        return TextInput.propTypes;
    }

    static get defaultProps() {
        return TextInput.defaultProps;
    }

    state = { opened: false, focusedOptionID: null };

    _optionsCache = {};

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps) {
        if (DEBUG) {
            this._validateChildren(nextProps);
        }
    }

    _validateChildren(props = this.props, children = props.children, usedIDs = {}) {
        toArray(children || []).flatten().filter(v => v).forEach(child => {
            if (child.type !== Label && child.type !== Option) {
                throw new Error('Children of EditableSelect must be instances of EditableSelect.Label or EditableSelect.Option.');
            }

            if (child.type === Option) {
                if (usedIDs[child.props.id]) {
                    throw new Error(`Duplicate id in EditableSelect.Option: ${child.props.id}`);
                }

                usedIDs[child.props.id] = true;
            }
        });
    }

    _onFocus = () => {
        this.setState({ opened: true });
    }

    _onTextChange = (sender, value) => {
        if (this.props.onChange) {
            this.props.onChange(this, value, null);
        }

        this.setState({ opened: !!value || this.state.opened, focusedOptionID: null });
    }

    _onSubmit = (sender, value) => {
        if (this.state.focusedOptionID) {
            this._onOptionChange(this, this.state.focusedOptionID);
        }
    }

    _onCancel = (sender) => {
        this.setState({ opened: false, focusedOptionID: null });
    }

    _onKeyDown = (event) => {
        switch (event.keyCode) {
            case 40:
            case 38:
                const optionIDs = Object.keys(this._optionsCache).map(Number);

                this.setState({
                    opened: true,
                    focusedOptionID: optionIDs.length ? +optionIDs[(optionIDs.length + optionIDs.indexOf(this.state.focusedOptionID) + event.keyCode - 39) % optionIDs.length] : null
                });
                break;

            default: return;
        }

        event.preventDefault();
    }

    _onOptionChange = (sender, id) => {
        if (this.props.onChange) {
            this.props.onChange(this, this._optionsCache[id], id);
        }

        this.setState({ opened: false });
    }

    _onBlur = (sender, value, event) => {
        if (this.refs.root.refs.popup && isAncestorOrSelf(this.refs.root.refs.popup.container, event.relatedTarget)) {
            event.target.focus();
            return;
        }

        this.setState({ opened: false, focusedOptionID: null });
    }

    _keyify(child, index) {
        return Array.isArray(child) ? child.map((subchild, subindex) => this._keyify(subchild, index + '.' + subindex)) :
            {
                key: child && child.key ? 'external-' + child.key : 'index-' + index.toString(),
                child
            };
    }

    render() {
        this._optionsCache = {};

        const
            { children, ...tiProps } = this.props,
            convertedChildren = toArray(children || [])
                .map((child, index) => this._keyify(child, index))
                .flatten()
                .filter(({ child }) => child)
                .map(({ child, key }) => {
                    if (child.type === Label) {
                        return <Listbox.Label key={key} {...child.props}>{child.props.children}</Listbox.Label>;
                    } else {
                        const { id, value, ...lbProps } = child.props;

                        if (!lbProps.disabled) {
                            this._optionsCache[id] = value;
                        }

                        return <Listbox.Option key={key} value={id} {...lbProps}>{child.props.children}</Listbox.Option>;
                    }
                });

        return (
            <DropPanel
                ref="root"
                className={'editable-select ' + this.props.className}
                opened={this.state.opened && !!convertedChildren.length}
                disabled={this.props.disabled}
                icon={null}
                title={
                    <div onKeyDown={this._onKeyDown}>
                        <TextInput {...tiProps} onFocus={this._onFocus} onChange={this._onTextChange} onSubmit={this._onSubmit} onCancel={this._onCancel} onBlur={this._onBlur} />
                    </div>
                }>
                <Listbox value={this.state.focusedOptionID} onChange={this._onOptionChange}>{convertedChildren}</Listbox>
            </DropPanel>
        );
    }
}